import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader, AlertCircle, Play, Pause, MessageSquare, Check, Clock, Phone, Info } from 'lucide-react';
import { createPortal } from 'react-dom';

// Re-using animation variants and helper functions if they are generic enough
// For simplicity, some might be redefined or adapted here.

const modalBackdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.2, delay: 0.1 } }
};

const modalContentVariants = {
  hidden: { scale: 0.95, opacity: 0, y: 20 },
  visible: { 
    scale: 1, 
    opacity: 1, 
    y: 0,
    transition: { type: "spring", damping: 30, stiffness: 300, duration: 0.4 }
  },
  exit: { 
    scale: 0.98, 
    opacity: 0, 
    y: 10,
    transition: { duration: 0.2 }
  }
};

const sectionVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1], delay: 0.1 } },
};


// Re-using statusConfig from CallDetailsPage if applicable, or a simplified version
const statusConfig = {
  issue_resolved: { color: 'text-emerald-600', background: 'bg-emerald-50', label: 'Resolved' },
  action_required: { color: 'text-amber-600', background: 'bg-amber-50', label: 'Action Required' },
  incomplete_call: { color: 'text-slate-600', background: 'bg-slate-50', label: 'Incomplete' },
  urgent_action: { color: 'text-red-600', background: 'bg-red-50', label: 'Urgent Action' },
  new_order: { color: 'text-purple-600', background: 'bg-purple-50', label: 'New Order' },
  unknown: { color: 'text-gray-600', background: 'bg-gray-50', label: 'Unknown' }
};


const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  const cleaned = phoneNumber.replace(/\D/g, '');
  if (cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'))) {
    const number = cleaned.length === 11 ? cleaned.slice(1) : cleaned;
    return `(${number.slice(0,3)}) ${number.slice(3,6)}-${number.slice(6)}`;
  }
  return phoneNumber;
};

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
  });
};

const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === null || seconds === undefined) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};


const CallDetailsModal = ({ isOpen, onClose, callId, supabase }) => {
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const [audioState, setAudioState] = useState({
    playing: false,
    currentTime: 0,
    duration: 0,
  });
  const [modalRootElement, setModalRootElement] = useState(null);

  useEffect(() => {
    // This effect should ideally only run once to set the modal root.
    // It's safe here because setModalRootElement will only update if modalRootElement is null.
    if (typeof document !== 'undefined') {
        let rootEl = document.getElementById('modal-root');
        if (!rootEl) {
          rootEl = document.createElement('div');
          rootEl.id = 'modal-root';
          document.body.appendChild(rootEl);
        }
        if (!modalRootElement) { // Only set if not already set
             setModalRootElement(rootEl);
        }
    }
  }, [modalRootElement]); // Dependency ensures it tries to set only if not already set.

  useEffect(() => {
    if (isOpen && callId) {
      fetchCallDetails(callId);
    } else {
      setCall(null);
      setError(null);
      setAudioState({ playing: false, currentTime: 0, duration: 0 });
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  }, [isOpen, callId]); // Supabase client is stable, fetchCallDetails is not in deps

  const fetchCallDetails = async (currentCallId) => { 
    setLoading(true);
    setError(null);
    setCall(null);
    try {
      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError) throw accountIdError;
      if (!accountId) throw new Error('Not authenticated');

      const { data, error: fetchError } = await supabase
        .from('calls')
        .select('*, contact:contacts(*), call_type:call_types(*)')
        .eq('id', currentCallId)
        .eq('account_id', accountId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!data) throw new Error('Call not found');

      setCall(data);
      if (data.recording_url && data.corrected_duration) {
        setAudioState(prev => ({ ...prev, duration: Math.round(parseFloat(data.corrected_duration)) }));
      } else if (data.recording_url) { // Removed audioRef.current check here as it might not be ready
         // Defer setting duration from metadata until audio element is available and metadata loaded
      }
    } catch (err) {
      console.error('Error fetching call details for modal:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Separate effect for audio metadata when call data is available
  useEffect(() => {
    if (call && call.recording_url && audioRef.current && !call.corrected_duration) {
      const audioElement = audioRef.current;
      const handleMetadata = () => {
        setAudioState(prev => ({ ...prev, duration: audioElement.duration }));
      };
      audioElement.addEventListener('loadedmetadata', handleMetadata);
      // If metadata already loaded (e.g. src changed on existing element)
      if (audioElement.readyState >= 1) {
        handleMetadata();
      }
      return () => {
        audioElement.removeEventListener('loadedmetadata', handleMetadata);
      };
    }
  }, [call]); // Runs when call object changes

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (audioState.playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(playError => console.error("Error playing audio:", playError));
    }
    setAudioState(prev => ({ ...prev, playing: !prev.playing }));
  };

  if (!isOpen || !modalRootElement) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[10002] overflow-y-auto flex items-center justify-center p-4">
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            variants={modalBackdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
          />
          <motion.div
            className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-200"
            variants={modalContentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-800">Call Details</h2>
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-full"
              >
                <X size={20} />
              </motion.button>
            </div>

            {/* Body */}
            <div className="flex-grow overflow-y-auto p-6">
              {loading && (
                <div className="flex flex-col items-center justify-center h-64">
                  <Loader className="w-8 h-8 text-blue-500 animate-spin mb-3" />
                  <p className="text-sm text-gray-500">Loading call information...</p>
                </div>
              )}
              {error && !loading && (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <AlertCircle className="w-10 h-10 text-red-500 mb-3" />
                  <p className="text-md font-medium text-red-700">Error loading call</p>
                  <p className="text-sm text-red-500 mt-1">{error}</p>
                   <button 
                        onClick={() => callId && fetchCallDetails(callId)} // Ensure callId is passed
                        className="mt-4 px-3 py-1.5 text-xs bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                    >
                        Try Again
                    </button>
                </div>
              )}
              {!loading && !error && call && (
                <motion.div 
                    className="space-y-6"
                    initial="initial"
                    animate="animate"
                    variants={{ animate: {transition: { staggerChildren: 0.1}} }}
                >
                  {/* Call Metadata */}
                  <motion.section variants={sectionVariants}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Caller</p>
                        <p className="text-sm font-medium text-gray-800 truncate">{call.contact?.name || formatPhoneNumber(call.from_number) || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Date & Time</p>
                        <p className="text-sm font-medium text-gray-800">{formatDate(call.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Category</p>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusConfig[call.call_category || 'unknown']?.background} ${statusConfig[call.call_category || 'unknown']?.color}`}>
                          {statusConfig[call.call_category || 'unknown']?.label || call.call_category?.replace(/_/g, ' ') || 'Unknown'}
                        </span>
                      </div>
                       <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Duration</p>
                        <p className="text-sm font-medium text-gray-800">{formatTime(audioState.duration)}</p>
                      </div>
                    </div>
                  </motion.section>

                  {/* Audio Player */}
                  {call.recording_url && (
                    <motion.section variants={sectionVariants}>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Recording</h3>
                      <div className="flex items-center gap-3 p-3 bg-gray-100 rounded-lg">
                        <button
                          onClick={handlePlayPause}
                          className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center flex-shrink-0 hover:bg-gray-800 transition-colors"
                        >
                          {audioState.playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
                        </button>
                        <div className="flex-grow relative h-1.5 bg-gray-300 rounded-full cursor-pointer"
                             onClick={(e) => {
                                 if (!audioRef.current || audioState.duration === 0) return;
                                 const rect = e.currentTarget.getBoundingClientRect();
                                 const clickX = e.clientX - rect.left;
                                 const newTime = (clickX / rect.width) * audioState.duration;
                                 audioRef.current.currentTime = newTime;
                                 setAudioState(prev => ({ ...prev, currentTime: newTime }));
                             }}
                        >
                          <motion.div 
                            className="absolute top-0 left-0 h-full bg-black rounded-full"
                            style={{ width: `${audioState.duration > 0 ? (audioState.currentTime / audioState.duration) * 100 : 0}%`}}
                          />
                        </div>
                        <span className="text-xs text-gray-600 tabular-nums w-20 text-right">
                          {formatTime(audioState.currentTime)} / {formatTime(audioState.duration)}
                        </span>
                      </div>
                      <audio
                        ref={audioRef}
                        src={call.recording_url}
                        onTimeUpdate={() => { if (audioRef.current) setAudioState(prev => ({ ...prev, currentTime: audioRef.current.currentTime }))}}
                        onLoadedMetadata={() => { // This will be handled by the separate useEffect for metadata
                        }}
                        onEnded={() => setAudioState(prev => ({ ...prev, playing: false, currentTime: 0 }))}
                        className="hidden"
                      />
                    </motion.section>
                  )}

                  {/* Summary */}
                  {call.summary && (
                    <motion.section variants={sectionVariants}>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Summary</h3>
                      <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 p-3 rounded-md border border-gray-100 whitespace-pre-wrap">{call.summary}</p>
                    </motion.section>
                  )}

                  {/* Transcript */}
                  {call.transcript && (
                    <motion.section variants={sectionVariants}>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Transcript</h3>
                      <div className="max-h-60 overflow-y-auto bg-gray-50 p-3 rounded-md border border-gray-100 space-y-3 text-sm">
                        {call.transcript.split('\n').map((line, index) => {
                           const isAI = line.toLowerCase().startsWith('assistant:') || line.toLowerCase().startsWith('bella:');
                           const isUser = line.toLowerCase().startsWith('user:') || line.toLowerCase().startsWith('caller:');
                           let speaker = 'System';
                           let message = line;

                           if (isAI) {
                               speaker = line.substring(0, line.indexOf(':')).trim();
                               message = line.substring(line.indexOf(':') + 1).trim();
                           } else if (isUser) {
                               speaker = call.contact?.name || formatPhoneNumber(call.from_number) || 'Caller';
                               message = line.substring(line.indexOf(':') + 1).trim();
                           }

                           if (!message.trim()) return null;

                           return (
                            <div key={index} className={`flex ${isAI ? 'justify-start' : 'justify-start'}`}>
                                <div className={`max-w-[85%] ${isAI ? 'pr-4' : 'pl-4'}`}>
                                    <p className={`text-xs font-medium mb-0.5 ${isAI ? 'text-blue-600' : 'text-gray-700'}`}>{speaker}</p>
                                    <p className={`p-2 rounded-lg text-xs ${isAI ? 'bg-blue-50 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>{message}</p>
                                </div>
                            </div>
                           );
                        })}
                      </div>
                    </motion.section>
                  )}

                </motion.div>
              )}
              {!loading && !error && !call && (
                 <div className="flex flex-col items-center justify-center h-64">
                  <Info className="w-10 h-10 text-gray-400 mb-3" />
                  <p className="text-sm text-gray-500">No call data to display.</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, modalRootElement);
};

export default CallDetailsModal; 